import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { TablePolices } from "../TablePolices"; // Usaremos a Tabela de Apólices para exibir os dados (assumindo que a estrutura é suficiente)
import { useNavigate } from "react-router-dom";
import type { PoliceTab } from "../TablePolices"; // Mantemos o tipo de tab para visualização de detalhes
import type { PoliceEvent } from "../../types/Polices";
import { PoliceMenu } from "../Policesmenu";
import { FireServices } from "../../../../shared/services/api/fire/FireServices";
import { DocumentTypeFilter, FireFinancialToolbar } from "../FireFinancilaToolbar";



// Função auxiliar mantida localmente, pois é usada apenas aqui
function getMonthDefaultRange() {
	const today = new Date();
	const ini = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
	const fim = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
	return { ini, fim };
}

// O componente agora se chama TabFireFinancial
export const TabFireFinancial = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const navigate = useNavigate();

	// --- ESTADOS: Filtros Financeiros ---
	const [documentType, setDocumentType] = useState<DocumentTypeFilter>("previa"); // Novo: Prévia, Prolabore, Fatura, Boletos
	const [imobiliariaSearch, setImobiliariaSearch] = useState(""); // Novo: Imobiliária Autocomplete

	// ESTADOS: Filtros Comuns (Controlados)
	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState(getMonthDefaultRange().ini);
	const [dataFim, setDataFim] = useState(getMonthDefaultRange().fim);
	const [status, setStatus] = useState("todos");

	// ESTADOS: Filtros Aplicados (Para disparo do useEffect)
	const [appliedFilters, setAppliedFilters] = useState({
		documentType: "previa" as DocumentTypeFilter,
		imobiliariaId: "", // Assumindo que o autocomplete retorna o ID
		text: "",
		dataIni: getMonthDefaultRange().ini,
		dataFim: getMonthDefaultRange().fim,
		status: "todos",
	});

	// ESTADOS: Dados e Paginação
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [data, setData] = useState<PoliceEvent[]>([]); // Mantemos PoliceEvent para a exibição na tabela
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	// ESTADOS: UI (Modal e Menu) - Mantidos para o contexto de apólice/detalhe
	const [modalState, setModalState] = useState({
		open: false,
		row: undefined as PoliceEvent | undefined,
		isNew: false,
		initialTab: "dados" as PoliceTab,
	});
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedMenuRow, setSelectedMenuRow] = useState<PoliceEvent | null>(null);

	const normalizeStatus = (raw: string) => (raw === "todos" ? "" : raw);

	// EFEITO DE BUSCA (Chama Serviço Financeiro Simulado)
	useEffect(() => {
		setIsLoading(true);
		const doFetch = () => {
			// **Adaptação:** Simulação de um novo serviço para buscar dados financeiros
			// Você precisará criar FireServices.getFinancials com os parâmetros apropriados
			FireServices.getFireQuotations(
				//appliedFilters.documentType,
				//appliedFilters.imobiliariaId,
				page + 1,
				rowsPerPage,
				appliedFilters.text,
				appliedFilters.dataIni,
				appliedFilters.dataFim,
				normalizeStatus(appliedFilters.status)
			)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content="Erro ao buscar registros financeiros" />,
						});
						return;
					}
					// Assumimos que a resposta contém dados que se encaixam em PoliceEvent para a tabela
					//setData(response.data);
					setTotalCount(response.totalCount ?? 0);
				})
				.finally(() => setIsLoading(false));
		};

		debounce(doFetch);
	}, [page, rowsPerPage, refreshTrigger, appliedFilters]);

	// FUNÇÃO ADAPTADA PARA APLICAR FILTROS
	const handleSearch = () => {
		// Simulação: Aqui você faria a conversão de imobiliariaSearch para imobiliariaId, se fosse um Autocomplete
		const imobiliariaId = imobiliariaSearch.length > 3 ? imobiliariaSearch : "";

		setAppliedFilters({
			documentType: documentType,
			imobiliariaId: imobiliariaId,
			text: textSearch ?? "",
			dataIni: dataIni,
			dataFim: dataFim,
			status: status,
		});
		setPage(0);
	};

	// --- Funções de Paginação e Menu (Mantidas) ---
	const handleChangePage = (newPage: number) => setPage(newPage);

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};

	const refreshList = () => setRefreshTrigger((prev) => prev + 1);
	const handleOpenNewRecord = () => navigate("/financeiro/novo"); // Ação adaptada para o contexto financeiro

	const handleOpenPolice = (id: string, tab?: PoliceTab) => {
		// Lógica de abertura de detalhes (mantida)
	};

	const handleCloseEdit = () => {
		// Lógica de fechar modal (mantida)
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: PoliceEvent) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedMenuRow(row);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
		setSelectedMenuRow(null);
	};

	const handleMenuSelect = (action: string) => {
		if (selectedMenuRow) {
			// Ação do menu (adapte a lógica de handleMenuAction para ações financeiras, se necessário)
			// handleMenuAction(action, selectedMenuRow.id);
		}
	};
	// ---------------------------------------------

	// RENDERIZAÇÃO
	return (
		<Box sx={{ pt: 1 }}>
			{/* TOOLBAR FINANCEIRA (NOVO COMPONENTE) */}
			<FireFinancialToolbar
				documentType={documentType}
				onChangeDocumentType={setDocumentType}
				imobiliariaSearch={imobiliariaSearch}
				onChangeImobiliariaSearch={setImobiliariaSearch} // Função para o Autocomplete/TextField
				showInputSearch
				showDateRange
				showStatusSelect
				showNewButton={false}
				textSearch={textSearch}
				onChangeTextSearch={setTextSearch}
				onClearTextSearch={() => setTextSearch("")}
				onChangeDateStart={setDataIni}
				onChangeDateEnd={setDataFim}
				onChangeStatus={setStatus}
				onClickRefresh={handleSearch}
				onClickNew={handleOpenNewRecord}
			/>
			<LoadingComp isopen={isLoading} />

			{/* TABELA (Reutilizando TablePolices para a estrutura) */}
			<TablePolices
				rows={data}
				totalCount={totalCount}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				onOpenPolice={handleOpenPolice}
				onOpenMenu={handleOpenMenu}
			/>

			{/* Menu (Reutilizando PoliceMenu, mas a lógica de ação deve ser revista para finaceiro) */}
			<PoliceMenu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleCloseMenu}
				selectedRow={selectedMenuRow}
				onSelect={handleMenuSelect}
			/>

			{/* Modal de detalhes (comentado, você deve criar o ModalFinancialDetails) */}
			{/* <ModalFinancialDetails
                open={modalState.open}
                onClose={handleCloseEdit}
                financialRecord={modalState.row ?? null}
            /> */}
		</Box>
	);
};
